import { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography
} from "@material-ui/core";
import {
  AssignmentTurnedIn,
  CalendarToday,
  PlayArrow,
  School
} from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  resetStartAbsiteDiagnostic,
  startAbsiteDiagnostic
} from "../../actions";
import Menu from "../../pages/menu/menu";
import { trackDiagnosticStarted } from "../../utils/tracking";
import "./absite-diagnostic.scss";

export default function AbsiteDiagnosticPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const startDiagnosticState = useSelector((state) => state.startAbsiteDiagnostic);
  const studentId = useSelector((state) => state.studentDetails?.userDetails?.id);
  const tokenUserId = useSelector((state) => state.userDetailsFromToken?.userDetails?.id);
  const userId = studentId || tokenUserId || window.localStorage.getItem("userId");

  const programCode = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("programCode")
      || queryParams.get("program")
      || queryParams.get("code")
      || queryParams.get("ref")
      || "";
  }, [location.search]);

  const pgyLevel = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("pgy") || "";
  }, [location.search]);

  useEffect(() => {
    const diagnosticQuiz = startDiagnosticState?.diagnosticQuiz;
    if (diagnosticQuiz?.id) {
      trackDiagnosticStarted({
        quizId: diagnosticQuiz.id,
        userId,
        programCode,
        pgyLevel
      });
      dispatch(resetStartAbsiteDiagnostic());
      history.push(`/exam/${diagnosticQuiz.id}/1`);
    }
  }, [dispatch, history, programCode, pgyLevel, startDiagnosticState, userId]);

  useEffect(() => {
    return () => dispatch(resetStartAbsiteDiagnostic());
  }, [dispatch]);

  const onStartDiagnostic = () => {
    if (!userId) {
      history.push(
        `/login?next=${encodeURIComponent(
          window.location.pathname + window.location.search
        )}`
      );
      return;
    }

    const payload = { userId };
    if (programCode) {
      payload.programCode = programCode;
    }
    if (pgyLevel) {
      payload.pgyLevel = pgyLevel;
    }

    dispatch(startAbsiteDiagnostic(payload));
  };

  return (
    <Box component="div" className="diagnostic-page">
      <Menu />
      <Box component="main" className="diagnostic-shell">
        <Box component="section" className="diagnostic-hero">
          <Box component="div" className="diagnostic-eyebrow">
            ABSITE readiness
          </Box>
          <Typography component="h1" className="diagnostic-title">
            Readiness diagnostic
          </Typography>
          <Typography component="p" className="diagnostic-copy">
            Original ABSITE-style questions with explanations for general surgery residents.
          </Typography>
          <Paper elevation={0} className="diagnostic-action-panel">
            <Box component="div" className="diagnostic-action-text">
              <Typography component="h2">40-question diagnostic</Typography>
              <Typography component="p">
                Complete the block, review your explanations, and open a focused study plan.
              </Typography>
            </Box>
            <Button
              className="diagnostic-start-button"
              color="primary"
              variant="contained"
              onClick={onStartDiagnostic}
              startIcon={startDiagnosticState?.loading ? <CircularProgress size={18} /> : <PlayArrow />}
              disabled={startDiagnosticState?.loading}
            >
              Start
            </Button>
          </Paper>
          {startDiagnosticState?.errMsg && (
            <Box component="div" className="diagnostic-error">
              {startDiagnosticState.errMsg}
            </Box>
          )}
        </Box>

        <Box component="section" className="diagnostic-metrics" aria-label="Diagnostic details">
          <Box component="div" className="diagnostic-metric">
            <AssignmentTurnedIn />
            <span>40 questions</span>
          </Box>
          <Box component="div" className="diagnostic-metric">
            <School />
            <span>Three weakest areas</span>
          </Box>
          <Box component="div" className="diagnostic-metric">
            <CalendarToday />
            <span>Seven-day plan</span>
          </Box>
        </Box>

        {programCode && (
          <Box component="div" className="diagnostic-program-code">
            Program code applied: <strong>{programCode}</strong>
          </Box>
        )}

        <Typography component="p" className="diagnostic-disclaimer">
          QLearn is not affiliated with or endorsed by the American Board of Surgery.
        </Typography>
      </Box>
    </Box>
  );
}
